import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import MaskedInput from 'react-input-mask';
import {
  beeStatusOptions,
  companyTypeOptions,
  industryOptions,
  turnoverOptions,
  yearsOperationalOptions,
} from '@features/workspace/views/company/utils/company-management';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import UseFormSelect from '@components/use-form-select';
import { FC, useMemo } from 'react';
import { parsePhoneNumber } from 'libphonenumber-js';
import { ensureFirstPlus } from '@utils/formaters';

const CustomerProfileEditCompanyDetails: FC<any> = ({
  data,
  register,
  errors,
  control,
}) => {
  const initialPhoneCountryCode = useMemo(() => {
    return data?.phoneNumber
      ? parsePhoneNumber(
          ensureFirstPlus(data?.phoneNumber)
        ).country?.toLowerCase()
      : 'za';
  }, []);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <Box display="flex">
        <Typography variant="legend">Company information</Typography>
      </Box>
      <Box display="flex" mt={2}>
        <Grid container spacing={9}>
          {/* Left side */}
          <Grid item md={6}>
            <TextField
              fullWidth
              label="Business name / trading as"
              placeholder="My awesome business"
              variant="outlined"
              {...register('tradingAs')}
              error={!!errors.tradingAs}
              helperText={errors?.tradingAs?.message}
            />
            <TextField
              fullWidth
              label="Registered business name"
              placeholder="My awesome business"
              variant="outlined"
              {...register('name')}
              error={!!errors.name}
              helperText={errors?.name?.message}
            />
            <UseFormSelect
              fullWidth
              control={control}
              error={!!errors.companyType}
              helperText={errors?.companyType?.message}
              label="Company type"
              name="companyType"
              variant="outlined"
            >
              {companyTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </UseFormSelect>

            <Controller
              control={control}
              name="taxNumber"
              render={({ field: { onChange, value } }) => (
                <MaskedInput
                  mask="9999999999"
                  value={value}
                  onChange={(event) => {
                    onChange(event.target.value.replaceAll('_', ''));
                  }}
                >
                  {/*  @ts-ignore Bug in lib https://stackoverflow.com/questions/72114032/cannot-use-react-input-mask-with-a-children-component-in-react */}
                  <TextField
                    fullWidth
                    error={!!errors.taxNumber}
                    helperText={errors?.taxNumber?.message}
                    label="Tax number"
                    placeholder="9999999999"
                    variant="outlined"
                  />
                </MaskedInput>
              )}
            />

            <Controller
              control={control}
              name="vatRegistrationNumber"
              render={({ field: { onChange, value } }) => (
                <MaskedInput
                  mask="4999999999"
                  value={value}
                  onChange={(event) => {
                    onChange(event.target.value.replaceAll('_', ''));
                  }}
                >
                  {/*  @ts-ignore */}
                  <TextField
                    fullWidth
                    error={!!errors.vatRegistrationNumber}
                    helperText={errors?.vatRegistrationNumber?.message}
                    label="VAT number"
                    placeholder="9999999999"
                    variant="outlined"
                  />
                </MaskedInput>
              )}
            />

            <Controller
              control={control}
              name="registrationNumber"
              render={({ field: { onChange, value } }) => (
                <MaskedInput
                  mask="9999/999999/99"
                  value={value}
                  onChange={(event) => {
                    onChange(event.target.value.replaceAll('_', ''));
                  }}
                >
                  {/*  @ts-ignore */}
                  <TextField
                    fullWidth
                    error={!!errors.registrationNumber}
                    helperText={errors?.registrationNumber?.message}
                    label="Registration number"
                    placeholder="9999/999999/99"
                    variant="outlined"
                  />
                </MaskedInput>
              )}
            />

            <Controller
              control={control}
              name="businessRegistrationDate"
              render={({ field: { onChange, value } }) => (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DesktopDatePicker
                    inputFormat="YYYY-MM-DD"
                    label="Business Registration Date"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!errors.businessRegistrationDate}
                        helperText={errors?.businessRegistrationDate?.message}
                      />
                    )}
                    value={value}
                    onChange={(event) => {
                      onChange(event);
                    }}
                  />
                </LocalizationProvider>
              )}
            />

            <UseFormSelect
              fullWidth
              control={control}
              error={!!errors.industry}
              helperText={errors?.industry?.message}
              label="Industry"
              name="industry"
              variant="outlined"
            >
              {industryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </UseFormSelect>

            <UseFormSelect
              fullWidth
              control={control}
              error={!!errors.businessBEEStatus}
              helperText={errors?.businessBEEStatus?.message}
              label="BEE Status"
              name="businessBEEStatus"
              variant="outlined"
            >
              {beeStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value ?? -1}>
                  {option.label}
                </MenuItem>
              ))}
            </UseFormSelect>
          </Grid>
          {/* Right side */}
          <Grid item md={6}>
            <UseFormSelect
              fullWidth
              control={control}
              error={!!errors.yearsOperational}
              helperText={errors?.yearsOperational?.message}
              label="Business age"
              name="yearsOperational"
              variant="outlined"
            >
              {yearsOperationalOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </UseFormSelect>

            <UseFormSelect
              fullWidth
              control={control}
              error={!!errors.annualTurnover}
              helperText={errors?.annualTurnover?.message}
              label="Annual revenue"
              name="annualTurnover"
              variant="outlined"
            >
              {turnoverOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </UseFormSelect>

            <FormControl fullWidth error={!!errors?.phoneNumber}>
              <FormLabel>Business contact number *</FormLabel>
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    country={initialPhoneCountryCode}
                    inputStyle={{
                      background: '#FFF',
                      border: '1px solid #EAEAEA',
                      borderRadius: '8px',
                      height: '48px',
                      color: '#4B4B4B',
                      width: '100%',
                    }}
                    placeholder="Business contact number"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              {errors.phoneNumber && (
                <FormHelperText>{errors?.phoneNumber?.message}</FormHelperText>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="Website"
              variant="outlined"
              {...register('website')}
              error={!!errors.website}
              helperText={errors?.website?.message}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CustomerProfileEditCompanyDetails;
